import { useState, useEffect } from 'react'

/**
 * Componente de carga de archivos con soporte para arrastrar y soltar (Drag & Drop).
 * * * Proporciona una interfaz visual para seleccionar múltiples archivos,
 * genera previsualizaciones locales para imágenes y gestiona la limpieza
 * automática de URLs temporales para evitar fugas de memoria.
 * * @component
 * @param {Object} props - Propiedades del componente.
 * @param {Array<Object>} [props.files=[]] - Estado actual de los archivos cargados.
 * @param {File} props.files[].file - Objeto File original del navegador.
 * @param {string|null} props.files[].preview - URL temporal (blob) para previsualización de imagen.
 * @param {string} props.files[].id - Identificador único generado para el archivo.
 * @param {Function} props.setFiles - Función para actualizar el estado de los archivos.
 * * @returns {JSX.Element} Un contenedor con zona de drop, input oculto y grid de previsualización.
 */
const FileDrop = ({ files = [], setFiles }) => {
  const [isDragging, setIsDragging] = useState(false)

  // =========================
  // HELPERS
  // =========================

  /**
   * Crea una URL de objeto temporal si el archivo es una imagen.
   * @param {File} file - El archivo del cual generar la previsualización.
   * @returns {string|null} URL del objeto o null si no es imagen.
   */
  const createPreview = (file) => {
    if (file.type.startsWith('image/')) {
      return URL.createObjectURL(file)
    }
    return null
  }

  /**
   * Transforma una lista de archivos File en objetos enriquecidos con ID y preview.
   * @param {File[]} fileList - Arreglo de archivos nativos.
   * @returns {Object[]} Arreglo de objetos formateados para el estado del componente.
   */
  const enhanceFiles = (fileList) => {
    return fileList.map((file) => ({
      file,
      preview: createPreview(file),
      id: `${file.name}-${file.size}-${Date.now()}`,
    }))
  }

  // =========================
  // EVENTS
  // =========================

  /**
   * Maneja el evento de soltar archivos en la zona designada.
   * @param {DragEvent} e - Evento de drop.
   */
  const handleDrop = (e) => {
    e.preventDefault()
    setIsDragging(false)
    const droppedFiles = Array.from(e.dataTransfer.files)
    setFiles((prev) => [...prev, ...enhanceFiles(droppedFiles)])
  }

  /**
   * Activa el estado visual de "arrastrando" sobre la zona.
   * @param {DragEvent} e - Evento de drag over.
   */
  const handleDragOver = (e) => {
    e.preventDefault()
    setIsDragging(true)
  }

  const handleDragLeave = () => {
    setIsDragging(false)
  }

  /**
   * Maneja la selección de archivos mediante el explorador de archivos estándar.
   * @param {ChangeEvent} e - Evento de cambio en el input tipo file.
   */
  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files)
    setFiles((prev) => [...prev, ...enhanceFiles(selectedFiles)])
  }

  /**
   * Elimina un archivo del estado local mediante su ID único.
   * @param {string} id - ID del archivo a remover.
   */
  const removeFile = (id) => {
    setFiles((prev) => prev.filter((f) => f.id !== id))
  }

  /**
   * Efecto de limpieza: Revoca las URLs de previsualización cuando el componente se desmonta
   * o cuando los archivos cambian para liberar recursos del navegador.
   */
  useEffect(() => {
    return () => {
      files.forEach((f) => {
        if (f.preview) URL.revokeObjectURL(f.preview)
      })
    }
  }, [files])

  return (
    <div className="file-drop-container">
      <label className="custom-label mb-2 d-block">
        ADJUNTOS (CAPTURAS O LOGS)
      </label>

      <div
        className={`custom-dropzone ${isDragging ? 'dragging' : ''}`}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={() => document.getElementById('fileInput').click()}
      >
        <input
          id="fileInput"
          type="file"
          multiple
          onChange={handleFileChange}
          style={{ display: 'none' }}
        />

        <div className="dropzone-content">
          <i className="fa-solid fa-cloud-arrow-up icon-upload"></i>
          <p className="mb-1 fw-medium">
            Arrastra archivos aquí o{' '}
            <span className="text-accent">haz clic</span>
          </p>
          <small className="text-muted-custom">
            PNG, JPG, PDF, LOG (Máx. 10MB)
          </small>
        </div>
      </div>

      {/* PREVIEW GRID */}
      {files.length > 0 && (
        <div className="mt-3">
          <div className="row g-2">
            {files.map(({ file, preview, id }) => (
              <div key={id} className="col-6 col-sm-4 col-md-3">
                <div className="file-preview-card">
                  {/* BOTÓN ELIMINAR */}
                  <button
                    type="button"
                    className="btn-remove"
                    onClick={(e) => {
                      e.stopPropagation()
                      removeFile(id)
                    }}
                  >
                    <i className="fa-solid fa-xmark"></i>
                  </button>

                  <div className="preview-media">
                    {preview ? (
                      <img
                        src={preview}
                        alt={file.name}
                        className="img-preview"
                      />
                    ) : (
                      <div className="file-icon">
                        <i className="fa-solid fa-file-lines fa-2x"></i>
                      </div>
                    )}
                  </div>

                  <div className="file-info">
                    <span className="file-name text-truncate">{file.name}</span>
                    <span className="file-size">
                      {(file.size / 1024).toFixed(1)} KB
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <style>{`
        /* Los colores salen de los tokens del Design System: el modo oscuro
           sale solo, sin un bloque aparte por cada regla. */
        .custom-dropzone {
          border: 2px dashed var(--border);
          border-radius: 12px;
          padding: 30px;
          text-align: center;
          cursor: pointer;
          transition: all 0.2s ease;
          background-color: var(--bg-surface-2);
          position: relative;
        }

        .custom-dropzone:hover, .custom-dropzone.dragging {
          border-color: var(--brand);
          background-color: var(--gm-primary-soft);
        }

        .icon-upload {
          font-size: 2rem;
          color: var(--text-muted);
          margin-bottom: 10px;
        }

        .text-accent { color: var(--brand); font-weight: 600; }

        /* Tarjetas de Preview */
        .file-preview-card {
          background: var(--bg-surface);
          border: 1px solid var(--border);
          border-radius: 10px;
          padding: 8px;
          position: relative;
          height: 100%;
          display: flex;
          flex-direction: column;
        }

        .preview-media {
          height: 80px;
          display: flex;
          align-items: center;
          justify-content: center;
          overflow: hidden;
          border-radius: 6px;
          background: var(--bg-surface-2);
          margin-bottom: 8px;
        }

        .img-preview {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .file-icon { color: var(--text-2); }

        .file-info {
          display: flex;
          flex-direction: column;
          gap: 2px;
        }

        .file-name {
          font-size: 0.75rem;
          font-weight: 600;
          color: var(--text);
        }

        .file-size {
          font-size: 0.7rem;
          color: var(--text-muted);
        }

        /* Botón Eliminar */
        .btn-remove {
          position: absolute;
          top: -5px;
          right: -5px;
          background: var(--gm-danger);
          color: #fff;
          border: none;
          width: 22px;
          height: 22px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 0.7rem;
          z-index: 10;
          box-shadow: 0 2px 5px rgba(0,0,0,0.2);
        }

        .btn-remove:hover { background: #b02a37; transform: scale(1.1); }
      `}</style>
    </div>
  )
}

export default FileDrop
